import type { Ingredient } from '@/types/ingredient.types';
import type { IngredientPreloadConfig, PreloadedIngredient } from '@/types/purchase-order.types';
import type { Location } from '@/context/AuthContext';

// Configuración por defecto para la precarga
export const DEFAULT_PRELOAD_CONFIG: IngredientPreloadConfig = {
  lowStockThreshold: 10, // Menos de 10 unidades se considera stock bajo
  autoIncludeExpiringSoon: true,
  daysUntilExpiry: 7, // 7 días para considerar próximo a vencer
  autoIncludeBestsellers: false, // Por defecto deshabilitado hasta tener datos de ventas
  defaultQuantityMultiplier: 2 // Sugerir el doble del umbral mínimo
};

// Tipo para agrupar ingredientes por ubicación
export interface LocationIngredientGroup {
  location: Location;
  ingredients: PreloadedIngredient[];
  totalCount: number;
  selectedCount: number;
  totalValue: number;
  isExpanded: boolean;
}

// Servicio para generar insumos precargados
export class IngredientPreloadService {
  
  static generatePreloadedIngredients(
    ingredients: Ingredient[],
    config: IngredientPreloadConfig = DEFAULT_PRELOAD_CONFIG
  ): PreloadedIngredient[] {
    const preloaded: PreloadedIngredient[] = [];
    
    for (const ingredient of ingredients) {
      const reasons = this.analyzeIngredient(ingredient, config);
      
      if (reasons.length > 0) {
        const suggestedQuantity = this.calculateSuggestedQuantity(ingredient, config, reasons[0]);
        
        // TODO: Cantidades aleatorias temporales - reemplazar con lógica real
        const randomQty = Math.floor(Math.random() * 20) + 1;
        const unitCost = ingredient.costPerUnit || ingredient.globalCostPerUnit || 0;
        preloaded.push({
          id: `preload-${ingredient.id}`,
          ingredient,
          suggestedQuantity,
          currentQuantity: randomQty,
          reason: reasons[0],
          isSelected: true,
          unitCost,
          totalCost: randomQty * unitCost
        });
      }
    }
    
    return preloaded.sort((a, b) => {
      // Ordenar por prioridad: stock bajo > próximo a vencer > bestsellers
      const priorityOrder = ['low_stock', 'expiring_soon', 'bestseller', 'manual'];
      return priorityOrder.indexOf(a.reason) - priorityOrder.indexOf(b.reason);
    });
  }
  
  private static analyzeIngredient(
    ingredient: Ingredient,
    config: IngredientPreloadConfig
  ): Array<'low_stock' | 'expiring_soon' | 'bestseller'> {
    const reasons: Array<'low_stock' | 'expiring_soon' | 'bestseller'> = [];

    // FILTRADO INTELIGENTE DESACTIVADO TEMPORALMENTE
    // Incluir TODOS los ingredientes sin filtros

    // Verificar stock bajo
    if (this.hasLowStock(ingredient, config)) {
      reasons.push('low_stock');
    }

    // Verificar próximo a vencer
    if (config.autoIncludeExpiringSoon && this.isExpiringSoon(ingredient, config)) {
      reasons.push('expiring_soon');
    }

    // Verificar bestseller (por implementar cuando tengamos datos de ventas)
    if (config.autoIncludeBestsellers && this.isBestseller(ingredient)) {
      reasons.push('bestseller');
    }

    // Si no hay razones específicas, agregar como 'manual' para incluir todos los ingredientes
    if (reasons.length === 0) {
      reasons.push('low_stock'); // Usar 'low_stock' como razón por defecto para mostrar todos
    }

    return reasons;
  }
  
  private static hasLowStock(ingredient: Ingredient, config: IngredientPreloadConfig): boolean {
    const currentStock = ingredient.currentStock || 0;
    const minStock = ingredient.minStock || config.lowStockThreshold;
    return currentStock <= minStock;
  }
  
  private static isExpiringSoon(ingredient: Ingredient, config: IngredientPreloadConfig): boolean {
    if (!ingredient.expirationDate) return false;
    
    const expirationDate = new Date(ingredient.expirationDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    return daysUntilExpiry <= config.daysUntilExpiry && daysUntilExpiry > 0;
  }
  
  private static isBestseller(ingredient: Ingredient): boolean {
    // Por implementar cuando tengamos datos de ventas
    // Por ahora devolver false
    return false;
  }
  
  private static calculateSuggestedQuantity(
    ingredient: Ingredient,
    config: IngredientPreloadConfig,
    reason: 'low_stock' | 'expiring_soon' | 'bestseller'
  ): number {
    const minStock = ingredient.minStock || config.lowStockThreshold;
    const currentStock = ingredient.currentStock || 0;
    
    switch (reason) {
      case 'low_stock':
        // Sugerir cantidad para alcanzar el stock óptimo
        const optimalStock = minStock * config.defaultQuantityMultiplier;
        return Math.max(optimalStock - currentStock, 0);

      case 'expiring_soon':
        // Para productos próximos a vencer, sugerir una cantidad conservadora
        return Math.max(Math.ceil(minStock * 0.5), 0);

      case 'bestseller':
        // Para bestsellers, sugerir cantidad basada en demanda (por implementar)
        return Math.max(minStock, 0);

      default:
        return 0;
    }
  }
  
  static updatePreloadedIngredient(
    preloadedIngredients: PreloadedIngredient[],
    ingredientId: string,
    updates: Partial<Pick<PreloadedIngredient, 'currentQuantity' | 'isSelected'>>
  ): PreloadedIngredient[] {
    return preloadedIngredients.map(item => {
      if (item.ingredient.id === ingredientId) {
        const updatedQuantity = updates.currentQuantity ?? item.currentQuantity;
        return {
          ...item,
          ...updates,
          currentQuantity: updatedQuantity,
          totalCost: updatedQuantity * item.unitCost
        };
      }
      return item;
    });
  }
  
  static getReasonLabel(reason: PreloadedIngredient['reason']): string {
    const labels = {
      low_stock: 'Stock Bajo',
      expiring_soon: 'Próximo a Vencer',
      bestseller: 'Más Vendido',
      manual: 'Agregado Manualmente'
    };
    return labels[reason];
  }
  
  static getReasonColor(reason: PreloadedIngredient['reason']): string {
    const colors = {
      low_stock: 'text-red-600',
      expiring_soon: 'text-yellow-600',
      bestseller: 'text-green-600',
      manual: 'text-blue-600'
    };
    return colors[reason];
  }

  // Método para generar grupos por ubicación (sin duplicados)
  static generateLocationGroups(
    ingredients: Ingredient[],
    locations: Location[],
    config: IngredientPreloadConfig = DEFAULT_PRELOAD_CONFIG
  ): LocationIngredientGroup[] {
    console.log('🔍 DEBUG - IngredientPreloadService.generateLocationGroups called with:', {
      ingredientsCount: ingredients.length,
      locationsCount: locations.length,
      firstIngredient: ingredients[0],
      firstLocation: locations[0]
    });

    const groups: LocationIngredientGroup[] = [];
    const isSingleLocation = locations.length === 1;
    const processedIngredients = new Set<string>();

    for (const location of locations) {
      // Filtrar ingredientes que pertenecen a esta ubicación
      const locationIngredients = ingredients.filter(ingredient => {
        // Solo evitar duplicados si es una sola ubicación
        if (isSingleLocation && processedIngredients.has(ingredient.id)) {
          return false;
        }

        const hasLocationStock = ingredient.locationStocks?.some(stock => stock.locationId === location.id);
        return hasLocationStock;
      });

      // Generar ingredientes precargados para esta ubicación
      const preloadedIngredients = this.generatePreloadedIngredientsForLocation(
        locationIngredients,
        location.id,
        config
      );

      // Solo marcar como procesados si es una sola ubicación
      if (isSingleLocation) {
        locationIngredients.forEach(ingredient => {
          processedIngredients.add(ingredient.id);
        });
      }

      if (preloadedIngredients.length > 0) {
        const selectedCount = preloadedIngredients.filter(item => item.isSelected && item.currentQuantity > 0).length;
        const totalValue = preloadedIngredients
          .filter(item => item.isSelected && item.currentQuantity > 0)
          .reduce((sum, item) => sum + item.totalCost, 0);

        groups.push({
          location,
          ingredients: preloadedIngredients,
          totalCount: preloadedIngredients.length,
          selectedCount,
          totalValue,
          isExpanded: true // Siempre expandir para que el usuario vea los ingredientes
        });
      }
    }

    // Ordenar grupos: primero los que tienen ingredientes críticos
    return groups.sort((a, b) => {
      const aCritical = a.ingredients.some(item =>
        item.reason === 'low_stock' || item.reason === 'expiring_soon'
      );
      const bCritical = b.ingredients.some(item =>
        item.reason === 'low_stock' || item.reason === 'expiring_soon'
      );

      if (aCritical && !bCritical) return -1;
      if (!aCritical && bCritical) return 1;
      return a.location.name.localeCompare(b.location.name);
    });
  }

  private static generatePreloadedIngredientsForLocation(
    ingredients: Ingredient[],
    locationId: string,
    config: IngredientPreloadConfig
  ): PreloadedIngredient[] {
    const preloaded: PreloadedIngredient[] = [];
    
    for (const ingredient of ingredients) {
      // Obtener el stock específico de esta ubicación
      const locationStock = ingredient.locationStocks?.find(stock => stock.locationId === locationId);
      if (!locationStock) continue;

      // Crear un ingrediente modificado con el stock de la ubicación específica
      const locationIngredient: Ingredient = {
        ...ingredient,
        currentStock: locationStock.currentStock,
        minStock: locationStock.minStock,
        maxStock: locationStock.maxStock
      };

      const reasons = this.analyzeIngredient(locationIngredient, config);
      
      if (reasons.length > 0) {
        const suggestedQuantity = this.calculateSuggestedQuantity(locationIngredient, config, reasons[0]);
        
        // TODO: Cantidades aleatorias temporales - reemplazar con lógica real
        const randomQty = Math.floor(Math.random() * 20) + 1;
        const unitCost = ingredient.costPerUnit || ingredient.globalCostPerUnit || 0;
        preloaded.push({
          id: `preload-${locationId}-${ingredient.id}`,
          ingredient: locationIngredient,
          suggestedQuantity,
          currentQuantity: randomQty,
          reason: reasons[0],
          isSelected: true,
          unitCost,
          totalCost: randomQty * unitCost,
          locationId
        });
      }
    }
    
    return preloaded.sort((a, b) => {
      const priorityOrder = ['low_stock', 'expiring_soon', 'bestseller', 'manual'];
      return priorityOrder.indexOf(a.reason) - priorityOrder.indexOf(b.reason);
    });
  }

  // Método para actualizar el estado expandido de un grupo
  static updateLocationGroupExpanded(
    groups: LocationIngredientGroup[],
    locationId: string,
    isExpanded: boolean
  ): LocationIngredientGroup[] {
    return groups.map(group => 
      group.location.id === locationId 
        ? { ...group, isExpanded }
        : group
    );
  }

  // Método para actualizar ingredientes dentro de un grupo
  static updateIngredientInGroup(
    groups: LocationIngredientGroup[],
    locationId: string,
    ingredientId: string,
    updates: Partial<Pick<PreloadedIngredient, 'currentQuantity' | 'isSelected'>>
  ): LocationIngredientGroup[] {
    return groups.map(group => {
      if (group.location.id === locationId) {
        const updatedIngredients = this.updatePreloadedIngredient(
          group.ingredients,
          ingredientId,
          updates
        );
        
        const selectedCount = updatedIngredients.filter(item => item.isSelected && item.currentQuantity > 0).length;
        const totalValue = updatedIngredients
          .filter(item => item.isSelected && item.currentQuantity > 0)
          .reduce((sum, item) => sum + item.totalCost, 0);

        return {
          ...group,
          ingredients: updatedIngredients,
          selectedCount,
          totalValue
        };
      }
      return group;
    });
  }
}